import {
  Navigate,
  RouterProvider,
  UIMatch,
  createBrowserRouter,
} from 'react-router-dom'
import { Shell } from 'Common/Layout/Layout'
import { TenantListPage } from 'Features/Tenant/TenantList.page'
import { ProjectsPage } from 'Features/Projects/Projects.page'
import { ProjectLayout } from 'Features/Projects/ProjectLayout'
import { lazy, Suspense } from 'react'
import { CircularProgress, Box } from '@mui/material'
import { projectCrumb, scenarioCrumb } from 'Common/Breadcrumbs/RouteCrumbs'

// Lazy load main navigation components
const ScenariosView = lazy(() =>
  import('Features/Projects/Scenarios/Scenarios.View').then((module) => ({
    default: module.ScenariosView,
  }))
)
const AnalysisView = lazy(() =>
  import('Features/Projects/Analysis/Analysis.View').then((module) => ({
    default: module.AnalysisView,
  }))
)
const ApprovalView = lazy(() =>
  import('Features/Projects/Approval/Approval.View').then((module) => ({
    default: module.ApprovalView,
  }))
)
const SnapshotView = lazy(() =>
  import('Features/Projects/Snapshot/Snapshot.View').then((module) => ({
    default: module.SnapshotView,
  }))
)

// Loading component for lazy loaded routes
const RouteLoader = () => (
  <Box
    display="flex"
    justifyContent="center"
    alignItems="center"
    minHeight="200px"
  >
    <CircularProgress />
  </Box>
)

// Wrapper component for lazy loaded routes
const LazyRoute = ({ children }: { children: React.ReactNode }) => (
  <Suspense fallback={<RouteLoader />}>{children}</Suspense>
)
import { EditSettingsView } from 'Features/Projects/Scenarios/EditSettings/EditSettings.view'
import { SalesRateView } from 'Features/Projects/Scenarios/EditSettings/PortfolioSettings/SalesRate.View'
import { ProductGroupingView } from 'Features/Projects/Scenarios/EditSettings/PortfolioSettings/ProductGrouping.View'
import { ClassificationView } from 'Features/Projects/Scenarios/EditSettings/PortfolioSettings/Classification.View'
import { ServiceLevelView } from 'Features/Projects/Scenarios/EditSettings/PortfolioSettings/ServiceLevel.View'
import { LocationGroupingView } from 'Features/Projects/Scenarios/EditSettings/NetworkSettings/LocationGrouping.View'
import { SiteStockFlowView } from 'Features/Projects/Scenarios/EditSettings/NetworkSettings/SiteStockFlow.View'
import { ReplenishmentView } from 'Features/Projects/Scenarios/EditSettings/NetworkSettings/Replenishment/Replenishment.View'
import { NetworkCapacitiesView } from 'Features/Projects/Scenarios/EditSettings/NetworkSettings/Capacities.View'
import { CapacitiesView } from 'Features/Projects/Scenarios/EditSettings/FactorySettings/Capacities.View'
import { AllocationsView } from 'Features/Projects/Scenarios/EditSettings/FactorySettings/Allocations.View'
import { ProductSettingsView } from 'Features/Projects/Scenarios/EditSettings/LineSettings/ProductSettings/ProductSettings.View'
import { ChangeoversView } from 'Features/Projects/Scenarios/EditSettings/LineSettings/Changeovers.View'
import { BatchSettingsView } from 'Features/Projects/Scenarios/EditSettings/LineSettings/BatchSettings/BatchSettings.View'
import { TotalProductionView } from 'Features/Projects/Scenarios/EditSettings/LineSettings/TotalProduction/TotalProduction.View'
import { CreateScenarioPage } from 'Features/Projects/Scenarios/CreateScenario.page'
import { TemplateResultsView } from 'Features/Projects/Scenarios/Templates/TemplateResults/TemplateResults.view'
import { PlanTemplateSummaryView } from 'Features/Projects/Scenarios/Templates/PlanTemplateSummary.view'
import { CreateProjectPage } from 'Features/Project/CreateProject/CreateProject.page'
import { OptimiserPage } from 'Features/Optimiser/Optimiser.page'
import { InventoryView } from 'Features/Projects/Scenarios/Inventory/Inventory.view'
import { InventoryResultsView } from 'Features/Projects/Scenarios/Inventory/Inventory.results.view'
import { ScenarioComparisonView } from 'Features/Projects/Analysis/ScenarioComparison.view'

// Each route that should appear in the shell's crumb trail names itself with
// `handle.crumb` (see Common/Breadcrumbs). Project and scenario names are
// looked up, so the trail reads "Projects / Q4 network / Base case / Sales rate".
const crumb = (label: string) => ({ crumb: () => label })

const router = createBrowserRouter([
  {
    path: '/',
    element: <Shell />,
    handle: crumb('Projects'),
    children: [
      { path: 'tenants', element: <TenantListPage /> },
      { path: 'select-tenant', element: <TenantListPage /> },
      { index: true, element: <ProjectsPage /> },
      { path: 'projects', element: <ProjectsPage /> },
      {
        path: 'create-project',
        element: <CreateProjectPage />,
        handle: crumb('Create project'),
      },
      {
        path: 'optimiser',
        element: <OptimiserPage />,
        handle: crumb('Optimiser'),
      },
      {
        path: 'projects/:projectId',
        element: <ProjectLayout />,
        handle: { crumb: projectCrumb },
        children: [
          {
            index: true,
            handle: crumb('Scenarios'),
            element: (
              <LazyRoute>
                <ScenariosView />
              </LazyRoute>
            ),
          },
          {
            path: 'scenarios',
            handle: crumb('Scenarios'),
            element: (
              <LazyRoute>
                <ScenariosView />
              </LazyRoute>
            ),
          },
          {
            path: 'create-scenario',
            element: <CreateScenarioPage />,
            handle: crumb('Create scenario'),
          },
          {
            path: 'scenarios/:scenarioId/scenario',
            element: <EditSettingsView />,
            handle: { crumb: scenarioCrumb },
            children: [
              {
                index: true,
                element: <Navigate to="template-summary" replace />,
              },
              {
                path: 'template-summary',
                element: <PlanTemplateSummaryView />,
                handle: crumb('Template summary'),
              },
              {
                path: 'sales-rate',
                element: <SalesRateView />,
                handle: crumb('Sales rate'),
              },
              {
                path: 'product-grouping',
                element: <ProductGroupingView />,
                handle: crumb('Product grouping'),
              },
              {
                path: 'classification',
                element: <ClassificationView />,
                handle: crumb('Classification'),
              },
              {
                path: 'service-level',
                element: <ServiceLevelView />,
                handle: crumb('Service level'),
              },
              {
                path: 'location-grouping',
                element: <LocationGroupingView />,
                handle: crumb('Location grouping'),
              },
              {
                path: 'site-stock-flow',
                element: <SiteStockFlowView />,
                handle: crumb('Site stock flow'),
              },
              {
                path: 'replenishment',
                element: <ReplenishmentView />,
                handle: crumb('Replenishment'),
              },
              {
                path: 'network-capacities',
                element: <NetworkCapacitiesView />,
                handle: crumb('Network capacities'),
              },
              {
                path: 'capacities',
                element: <CapacitiesView />,
                handle: crumb('Capacities'),
              },
              {
                path: 'allocations',
                element: <AllocationsView />,
                handle: crumb('Allocations'),
              },
              {
                path: 'batch-production',
                element: <BatchSettingsView />,
                handle: crumb('Batch production'),
              },
              {
                path: 'sku-production',
                element: <ProductSettingsView />,
                handle: crumb('SKU production'),
              },
              {
                path: 'total-production',
                element: <TotalProductionView />,
                handle: crumb('Total production'),
              },
              {
                path: 'changeovers',
                element: <ChangeoversView />,
                handle: crumb('Changeovers'),
              },
              {
                path: 'template-summary/template-results/:workcentreId',
                element: <TemplateResultsView />,
                handle: crumb('Template results'),
              },
              {
                // The old per-method URL: opens that method's tabs.
                path: 'template-summary/template-results/:workcentreId/:preferredTemplate/template',
                element: <TemplateResultsView />,
                handle: crumb('Template results'),
              },
              {
                path: 'inventory-summary',
                element: <InventoryView />,
                handle: crumb('Inventory summary'),
              },
              {
                path: 'inventory-results/:siteKey',
                element: <InventoryResultsView />,
                handle: { crumb: (match: UIMatch) => match.params.siteKey },
              },
            ],
          },
          {
            path: 'analysis',
            handle: crumb('Analysis'),
            element: (
              <LazyRoute>
                <AnalysisView />
              </LazyRoute>
            ),
            children: [
              {
                index: true,
                element: <Navigate to="scenario-comparison" replace />,
              },
              {
                path: 'scenario-comparison',
                element: <ScenarioComparisonView />,
                handle: crumb('Scenario comparison'),
              },
            ],
          },
          {
            path: 'approval',
            handle: crumb('Approval'),
            element: (
              <LazyRoute>
                <ApprovalView />
              </LazyRoute>
            ),
          },
          {
            path: 'snapshot',
            handle: crumb('Snapshot'),
            element: (
              <LazyRoute>
                <SnapshotView />
              </LazyRoute>
            ),
          },
        ],
      },
    ],
  },
])

export const AppRoutes = () => {
  return <RouterProvider router={router} />
}
